import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'
import { Badge, Card, EmptyState, Spinner, Table } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { ConfirmAppointmentButton } from './ConfirmAppointmentButton'
import { upcomingFirst, type PortalAppointment } from './appointments'
import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  formatDate,
  formatDateTime,
  formatTime,
} from '../../utils/format'

/** Mis citas: próximas arriba, historial abajo. */
export function MisCitasPage() {
  const appointments = useQuery({
    queryKey: ['patient-appointments'],
    queryFn: async () => (await api.get<PortalAppointment[]>('/patient-portal/appointments')).data,
  })

  const now = new Date().toISOString()
  const all = appointments.data ?? []
  const upcoming = upcomingFirst(all, now)
  const history = all.filter((a) => a.startsAt < now || ['CANCELLED', 'NO_SHOW'].includes(a.status))

  return (
    <>
      <Card title="Próximas citas">
        {appointments.isLoading ? (
          <Spinner />
        ) : upcoming.length === 0 ? (
          <EmptyState message="No tienes citas agendadas. Contáctanos por WhatsApp o teléfono para reservar." />
        ) : (
          <div className="space-y-2">
            {upcoming.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 p-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink">{a.service.name}</p>
                  <p className="text-sm text-gray-600">
                    {formatDate(a.startsAt)} a las {formatTime(a.startsAt)} · {a.professional.firstName}{' '}
                    {a.professional.lastName}
                  </p>
                  {/* Dónde ocurre la cita depende de la modalidad (H7). */}
                  {a.modality === 'TELEMEDICINE' ? (
                    <p className="text-xs text-gray-500">Atención por videollamada</p>
                  ) : a.modality === 'HOME_VISIT' ? (
                    <p className="text-xs text-gray-500">
                      Visita a domicilio{a.visitAddress ? ` — ${a.visitAddress}` : ''}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">
                      {a.location.name}
                      {a.location.address ? ` — ${a.location.address}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {a.modality === 'TELEMEDICINE' && a.videoCallUrl && (
                    <a
                      href={a.videoCallUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                    >
                      <Icon name="video" className="h-4 w-4" />
                      Entrar a la videollamada
                    </a>
                  )}
                  {/* Confirmar la hora también desde acá: el paciente que entra
                      a "Mis citas" no debería tener que buscarla en Controles. */}
                  <ConfirmAppointmentButton appointmentId={a.id} status={a.status} startsAt={a.startsAt} />
                  <Badge className={APPOINTMENT_STATUS_COLORS[a.status]}>{APPOINTMENT_STATUS_LABELS[a.status]}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Historial de citas">
        {history.length === 0 ? (
          <EmptyState message="Sin citas anteriores." />
        ) : (
          <Table headers={['Fecha', 'Prestación', 'Profesional', 'Estado']}>
            {history.map((a) => (
              <tr key={a.id}>
                <td className="px-2 py-2 text-gray-600">{formatDateTime(a.startsAt)}</td>
                <td className="px-2 py-2">{a.service.name}</td>
                <td className="px-2 py-2 text-gray-600">
                  {a.professional.firstName} {a.professional.lastName}
                </td>
                <td className="px-2 py-2">
                  <Badge className={APPOINTMENT_STATUS_COLORS[a.status]}>{APPOINTMENT_STATUS_LABELS[a.status]}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  )
}
